import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Package, Power, PowerOff } from 'lucide-react';
import type { Ingredient } from '@/types/ingredient.types';

interface ToggleActiveModalProps {
  isOpen: boolean;
  ingredient: Ingredient | null;
  onClose: () => void;
  onConfirm: () => void;
  loading?: boolean;
}

export const ToggleActiveModal: React.FC<ToggleActiveModalProps> = ({
  isOpen,
  ingredient,
  onClose,
  onConfirm,
  loading = false
}) => {
  if (!ingredient) return null;

  const isReactivation = !ingredient.isActive;

  const getTitle = () => {
    return isReactivation ? 'Confirmar Reactivación' : 'Confirmar Desactivación';
  };

  const getActionText = () => {
    return isReactivation ? 'reactivar' : 'desactivar';
  };

  const getActionTextCapitalized = () => {
    return isReactivation ? 'Reactivar' : 'Desactivar';
  };

  const getLoadingText = () => {
    return isReactivation ? 'Reactivando...' : 'Desactivando...';
  };

  const getWarningMessage = () => {
    return isReactivation
      ? 'Este insumo volverá a estar activo y disponible para su uso en recetas y órdenes de compra.'
      : 'Este insumo quedará inactivo y no estará disponible para nuevos usos. Los registros históricos no se verán afectados. Podrás reactivarlo más adelante.';
  };

  const getColorClasses = () => {
    if (isReactivation) {
      return {
        bg: 'bg-green-50 border-green-200',
        icon: 'text-green-600',
        title: 'text-green-800',
        text: 'text-green-700',
        button: 'bg-green-600 hover:bg-green-700'
      };
    }
    return {
      bg: 'bg-orange-50 border-orange-200',
      icon: 'text-orange-600',
      title: 'text-orange-800',
      text: 'text-orange-700',
      button: 'bg-orange-600 hover:bg-orange-700'
    };
  };

  const colors = getColorClasses();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            {getTitle()}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-center">
          <Package className={`h-12 w-12 ${isReactivation ? 'text-green-500' : 'text-orange-500'} mx-auto`} />

          <div className="space-y-2">
            <p className="text-lg font-medium">
              ¿Estás seguro de que deseas {getActionText()} este insumo?
            </p>

            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="font-semibold text-gray-900">
                {ingredient.name}
              </p>
              {ingredient.description && (
                <p className="text-sm text-gray-600 mt-1">
                  {ingredient.description}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Categoría: {ingredient.categoryName || ingredient.category}
              </p>
            </div>
          </div>

          <div className={`flex items-start gap-2 p-3 ${colors.bg} border rounded-lg text-left`}>
            {isReactivation ? (
              <Power className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
            ) : (
              <PowerOff className={`h-5 w-5 ${colors.icon} mt-0.5 flex-shrink-0`} />
            )}
            <div>
              <p className={`font-semibold ${colors.title} text-sm`}>
                Información
              </p>
              <p className={`text-sm ${colors.text} mt-1`}>
                {getWarningMessage()}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className={colors.button}
          >
            {loading ? getLoadingText() : getActionTextCapitalized()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
